import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Bot, CalendarCheck2, RotateCcw, MessageCircle, Play } from "lucide-react";
import { openCalendly } from "@/integrations/calendly";
import { track } from "@/lib/analytics";

const autoScript = [
  { from: "user", text: "Hi, I saw your listing on Maple Street online. Is it still available? I'd love to tour it this week." },
  { from: "agent", text: "Hi Sarah! Thanks for reaching out about the Maple Street home — yes, it's still available. Are you hoping to tour soon, or just starting your search?" },
  { from: "user", text: "I'd like to see it soon. Thursday or Friday would work." },
  { from: "agent", text: "Great. Quick question so I can set up the right visit: are you already pre-approved, or still exploring financing?" },
  { from: "user", text: "I'm pre-approved." },
  { from: "agent", text: "That puts you in a strong position. We have showing windows Thursday afternoon and Friday morning. Would you like me to reserve one?" },
  { from: "user", text: "Yes, Thursday." },
];

const autoOutcome = "Thursday showing selected — appointment ready to schedule";

const introMsg =
  "Hi! Thanks for reaching out about the Maple Street listing. Are you hoping to tour the home soon, or just starting your search?";

const FLOWS = {
  soon: {
    reply: "Wonderful — it's a great home. Quick question so I can set up the right visit: are you already pre-approved, or still exploring financing?",
    chips: ["I'm pre-approved", "Still exploring financing"],
    next: {
      "I'm pre-approved": {
        reply: "That puts you in a strong position. We have showing windows Thursday afternoon and Saturday morning. Which works better for you?",
        chips: ["Thursday afternoon", "Saturday morning"],
        next: {
          "Thursday afternoon": {
            reply: "You're set for Thursday afternoon. I've sent a confirmation text with the address and details.",
            outcome: "Showing booked for Thursday afternoon — confirmation sent",
          },
          "Saturday morning": {
            reply: "You're set for Saturday morning. I've sent a confirmation text with the address and details.",
            outcome: "Showing booked for Saturday morning — confirmation sent",
          },
        },
      },
      "Still exploring financing": {
        reply: "No problem — many buyers start right here. A quick buyer consultation can help you understand your budget before touring. Would you like to schedule one?",
        chips: ["Book a consultation", "Send me listings first"],
        next: {
          "Book a consultation": {
            reply: "You're on the calendar. I've texted the confirmation along with a short prep list.",
            outcome: "Buyer consultation booked — confirmation sent",
          },
          "Send me listings first": {
            reply: "Done — I'll send a shortlist of similar homes in the area and check in with you next week.",
            outcome: "Listing shortlist on the way — follow-up scheduled",
          },
        },
      },
    },
  },
  early: {
    reply: "That's a smart place to start. So I can send you the right homes, what's your rough timeline?",
    chips: ["1–3 months", "3–6 months", "Just browsing"],
    next: {
      "1–3 months": {
        reply: "That's a great window to get prepared. I'd recommend a quick consultation now so you're ready to move fast when the right home appears.",
        chips: ["Book a consultation", "Send me listings first"],
        next: {
          "Book a consultation": {
            reply: "You're on the calendar. I've texted the confirmation along with a short prep list.",
            outcome: "Buyer consultation booked — confirmation sent",
          },
          "Send me listings first": {
            reply: "Done — I'll send homes matching your timeline and area, and check in as new listings hit the market.",
            outcome: "Added to listing alerts — follow-up scheduled",
          },
        },
      },
      "3–6 months": {
        reply: "Good planning gives you an edge. I'll send monthly market updates and new listings, and we can talk strategy when you're closer.",
        chips: ["Sounds good"],
        next: {
          "Sounds good": {
            reply: "You're on the list. When your timeline moves up, we'll get you touring quickly.",
            outcome: "Added to nurture plan — market updates scheduled",
          },
        },
      },
      "Just browsing": {
        reply: "No pressure at all. I'll keep you posted on new listings and price changes in the area so you can watch the market.",
        chips: ["Keep me posted"],
        next: {
          "Keep me posted": {
            reply: "You're all set. And when something catches your eye, I'm one message away.",
            outcome: "Added to listing alerts — follow-up scheduled",
          },
        },
      },
    },
  },
};

function Bubble({ from, children }) {
  const isAgent = from === "agent";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 26 }}
      className={`flex ${isAgent ? "" : "justify-end"}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm md:text-base leading-relaxed ${
          isAgent
            ? "bg-gradient-to-br from-indigo-600/80 to-indigo-700/80 rounded-bl-sm text-white"
            : "bg-white/10 rounded-br-sm text-slate-100"
        }`}
      >
        {children}
      </div>
    </motion.div>
  );
}

function Typing() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex">
      <div className="bg-white/10 rounded-2xl rounded-bl-sm px-4 py-3.5 flex gap-1.5">
        <span className="typing-dot h-2 w-2 rounded-full bg-slate-300" />
        <span className="typing-dot h-2 w-2 rounded-full bg-slate-300" />
        <span className="typing-dot h-2 w-2 rounded-full bg-slate-300" />
      </div>
    </motion.div>
  );
}

export default function LiveDemo() {
  const prefersReduced = useReducedMotion();
  const cardRef = useRef(null);
  const scrollRef = useRef(null);
  const timers = useRef([]);

  const [mode, setMode] = useState("auto"); // "auto" | "interactive"
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [chips, setChips] = useState([]);
  const [stage, setStage] = useState(0); // interactive: 0 intent, 1+ branch steps
  const [node, setNode] = useState(null); // current branch node
  const [outcome, setOutcome] = useState(null);
  const [finished, setFinished] = useState(false);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const playAuto = (instant = false) => {
    clearTimers();
    setMode("auto");
    setChips([]);
    setOutcome(null);
    setStage(0);
    setNode(null);
    setTyping(false);
    if (instant || prefersReduced) {
      setMessages(autoScript);
      setOutcome(autoOutcome);
      setFinished(true);
      return;
    }
    setMessages([]);
    setFinished(false);
    track("demo_auto_start");
    let t = 700;
    autoScript.forEach((m) => {
      if (m.from === "agent") {
        later(() => setTyping(true), t);
        t += 900;
        later(() => {
          setTyping(false);
          setMessages((prev) => [...prev, m]);
        }, t);
        t += 1600;
      } else {
        later(() => setMessages((prev) => [...prev, m]), t);
        t += 1800;
      }
    });
    later(() => setOutcome(autoOutcome), t);
    t += 700;
    later(() => {
      setFinished(true);
      track("demo_auto_complete");
    }, t);
  };

  const startInteractive = () => {
    clearTimers();
    setMode("interactive");
    setMessages([]);
    setOutcome(null);
    setFinished(false);
    setStage(0);
    setNode(null);
    setChips([]);
    const show = () => {
      setTyping(false);
      setMessages([{ from: "agent", text: introMsg }]);
      setChips(["Tour this week", "Just starting my search"]);
    };
    if (prefersReduced) {
      show();
      return;
    }
    setTyping(true);
    later(show, 900);
  };

  const pick = (chip) => {
    setMessages((m) => [...m, { from: "user", text: chip }]);
    setChips([]);
    track("demo_interactive_choice", chip);
    const advance = () => {
      if (stage === 0) {
        const branchKey = chip === "Tour this week" ? "soon" : "early";
        track(chip === "Tour this week" ? "demo_tour_this_week" : "demo_just_starting_search");
        const branch = FLOWS[branchKey];
        setMessages((m) => [...m, { from: "agent", text: branch.reply }]);
        setChips(branch.chips);
        setNode(branch);
        setStage(1);
        return;
      }
      const nextNode = node?.next?.[chip];
      if (!nextNode) return;
      setMessages((m) => [...m, { from: "agent", text: nextNode.reply }]);
      if (nextNode.chips) {
        setChips(nextNode.chips);
        setNode(nextNode);
      } else {
        setOutcome(nextNode.outcome);
        setFinished(true);
        track("demo_interactive_booking", chip);
      }
    };
    if (prefersReduced) {
      advance();
      return;
    }
    setTyping(true);
    later(() => {
      setTyping(false);
      advance();
    }, 1200);
  };

  // Auto-play once when the demo card first enters the viewport
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          playAuto();
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing, chips, outcome]);

  return (
    <section id="how-it-works" data-testid="live-demo-section" className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[#0B0F19]" />

      <div className="relative max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            From New Inquiry to <span className="text-gradient">Booked Showing</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl mx-auto">
            A buyer reaches out about a listing. You respond quickly, ask the right questions,
            follow up, and help them take the next step — even when you're at another showing.
          </p>
          <p data-testid="demo-watch-hint" className="mt-6 text-base font-semibold text-cyan-300">
            Watch the example below — no clicking required.
          </p>
          <div data-testid="demo-context-tags" className="mt-6 flex flex-wrap justify-center gap-2.5">
            <span className="rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 text-xs font-semibold px-3.5 py-1.5">
              New listing inquiry — Maple Street
            </span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">
              Responds in seconds
            </span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">
              Qualifies the buyer
            </span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">
              Books the showing
            </span>
          </div>
        </motion.div>

        <motion.div
          ref={cardRef}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="glass rounded-3xl overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.5)]"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.03]">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
                <Bot size={20} />
              </div>
              <div>
                <p className="font-semibold text-sm flex items-center gap-2">
                  Live Demonstration
                  <span className="rounded-full bg-cyan-500/15 text-cyan-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                    Example
                  </span>
                </p>
                <p className="text-xs text-slate-300 mt-0.5">
                  Watch how a new inquiry becomes a booked showing.
                </p>
              </div>
            </div>
            <button
              data-testid="demo-reset-button"
              data-track="demo_replay_icon"
              onClick={() => playAuto()}
              className="text-slate-400 hover:text-cyan-300 transition-colors duration-200 p-2 rounded-full hover:bg-white/5"
              aria-label="Replay demo"
              title="Replay demo"
            >
              <RotateCcw size={18} />
            </button>
          </div>

          <div ref={scrollRef} data-testid="demo-chat-window" className="h-[380px] overflow-y-auto p-5 space-y-3.5">
            <AnimatePresence>
              {messages.map((m, i) => (
                <Bubble key={`${mode}-${i}`} from={m.from}>{m.text}</Bubble>
              ))}
              {typing && <Typing key="typing" />}
              {outcome && (
                <motion.div
                  key="outcome"
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex justify-center pt-1"
                >
                  <div
                    data-testid="demo-outcome-card"
                    className="flex items-center gap-2.5 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-sm font-semibold px-5 py-3 text-center"
                  >
                    <CalendarCheck2 size={18} className="shrink-0" />
                    {outcome}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {messages.length === 0 && !typing && (
              <p className="text-slate-500 text-sm text-center pt-32">The example conversation will begin shortly…</p>
            )}
          </div>

          <div className="px-5 py-4 border-t border-white/10 bg-white/[0.03] min-h-[76px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              {chips.length > 0 ? (
                <motion.div
                  key={chips.join()}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex flex-wrap justify-center gap-2.5"
                >
                  <p className="w-full text-center text-xs text-slate-400 mb-0.5">Tap a reply to continue:</p>
                  {chips.map((c) => (
                    <button
                      key={c}
                      data-testid={`demo-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => pick(c)}
                      className="rounded-full px-6 py-3 text-sm font-semibold bg-indigo-600/20 border border-indigo-400/40 text-indigo-200 hover:bg-indigo-600/50 hover:border-indigo-300 hover:-translate-y-0.5 transition-[background-color,border-color,transform] duration-200"
                    >
                      {c}
                    </button>
                  ))}
                </motion.div>
              ) : finished ? (
                <motion.div
                  key="controls"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center gap-2.5 w-full"
                >
                  <p className="text-sm text-slate-300">Want to experience it? Start a sample conversation.</p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <button
                      data-testid="demo-try-button"
                      data-track="demo_try_click"
                      onClick={startInteractive}
                      className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                    >
                      <MessageCircle size={16} />
                      Try the Demo Yourself
                    </button>
                    <button
                      data-testid="demo-replay-button"
                      data-track="demo_replay_click"
                      onClick={() => playAuto()}
                      className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white/5 border border-white/15 hover:border-cyan-400/50 hover:text-cyan-300 text-sm font-semibold text-slate-200 transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5"
                    >
                      <Play size={16} />
                      Replay Demo
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.p
                  key="hint"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-slate-400 text-center"
                  data-testid="demo-status-hint"
                >
                  {mode === "auto" ? "Playing example conversation…" : "The agent is responding…"}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-10 text-center"
        >
          <button
            type="button"
            onClick={() => openCalendly()}
            data-testid="demo-book-cta-button"
            data-track="cta_book_call_demo"
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(99,102,241,0.45)]"
          >
            Book Your Lead Flow Review
          </button>
        </motion.div>
      </div>
    </section>
  );
}
